import { Editor, OnMount } from "@monaco-editor/react";
import * as monaco from "monaco-editor";
import { useEffect, useRef, useState } from "react";
import {
  CompletionItemProvider,
  getLanguageConfig,
  TokenProvider
} from "./editorLangConfig";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { encodeToBase64 } from "@lxp-app/utils/encodeImperva";
import { useDebouncedCallback } from "use-debounce";

interface EditorProps {
  autoSaveApi: () => void;
  questionData?: any;
  editorObj: any;
  editorTheme: "vs-dark" | "light";
  showTestCases: boolean;
  editorLanguage: any;
  savedCodeList?: any[];
  setIsCodeChange: (arg: boolean) => void;
  setEditorObj: (arg: any) => void;
  resetToStarterCode: () => void;
}

const langObj: { [key: string]: string } = {
  Java: "myJava",
  "C++": "myCpp",
  Javascript: "javascript",
  C: "myC",
  "C#": "myCSharp",
  Python: "myPython",
  Go: "myGo",
  Typescript: "myTypeScript"
};

const langs: string[] = [
  "myJava",
  "myPython",
  "myCpp",
  "myC",
  "myGo",
  "myCSharp",
  "myTypeScript"
];

const EditorComponent = ({
  autoSaveApi,
  questionData,
  editorObj,
  editorTheme,
  showTestCases,
  editorLanguage,
  savedCodeList,
  setIsCodeChange,
  setEditorObj,
  resetToStarterCode
}: EditorProps) => {
  const dispatch = useAppDispatch();

  const isNotProd = ["dev", "development", "staging"].includes(
    import.meta.env.MODE
  );

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const [langCodeObj, setLangCodeObj] = useState<any>({});
  const [langSelected, setLangSelected] = useState<any>("");

  const monacoRef = useRef<typeof monaco | null>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const isInternalCopyCut = useRef(false);

  // true = we are setting value programmatically, skip editorChange dispatch
  const isExternalUpdateRef = useRef(false);
  // track if the current onChange came from user typing
  const isUserTypingRef = useRef(false);

  const prevQuestionIdRef = useRef<string>("");
  const prevLangRef = useRef<string>("");

  const debounced: any = useDebouncedCallback((value: any) => {
    editorChange(value);
  }, 100);

  // Helper to set editor value imperatively without triggering editorChange
  const setEditorValueImperatively = (value: string) => {
    if (!editorRef.current) return;
    const currentValue = editorRef.current.getValue();
    if (currentValue === value) return; // no-op if same
    isExternalUpdateRef.current = true;
    editorRef.current.setValue(value);
    isExternalUpdateRef.current = false;
  };

  useEffect(() => {
    isInternalCopyCut.current = false;
    setLangCodeObj({});
  }, [questionData]);

  useEffect(() => {
    setLangSelected(langObj[editorLanguage?.value]);
  }, [editorLanguage]);

  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      const model = editorRef.current.getModel();
      if (model) {
        monacoRef.current.editor.setModelLanguage(model, langSelected);
      }
    }
  }, [langSelected]);

  // Sync editorObj → editor imperatively ONLY when question or language switches
  // This retains value without causing cursor jump on typing
  useEffect(() => {
    if (!editorRef.current) return;

    const questionChanged =
      prevQuestionIdRef.current !== questionData?.questionId;
    const langChanged = prevLangRef.current !== editorLanguage?.value;

    if (questionChanged || langChanged) {
      prevQuestionIdRef.current = questionData?.questionId ?? "";
      prevLangRef.current = editorLanguage?.value ?? "";

      const newValue = editorObj?.[questionData?.questionId] ?? "";
      setEditorValueImperatively(newValue);
    }
  }, [editorObj, editorLanguage?.value, questionData?.questionId]);

  // Also sync when editorObj updates externally (e.g. saved code loaded)
  // but only if user is NOT currently typing
  useEffect(() => {
    if (!editorRef.current) return;
    if (isUserTypingRef.current) return; // user is typing, don't interfere

    const newValue = editorObj?.[questionData?.questionId] ?? "";
    setEditorValueImperatively(newValue);
  }, [editorObj]);

  const handleEditorDidMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoRef.current = monacoInstance;

    prevQuestionIdRef.current = questionData?.questionId ?? "";
    prevLangRef.current = editorLanguage?.value ?? "";

    // Set initial value on mount imperatively
    const initialValue = editorObj?.[questionData?.questionId] ?? "";
    if (initialValue) {
      isExternalUpdateRef.current = true;
      editor.setValue(initialValue);
      isExternalUpdateRef.current = false;
    }

    if (!isNotProd) {
      // Disable copy and paste in production environment
      editor.addCommand(
        monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyC,
        () => {
          isInternalCopyCut.current = true;
          editor.trigger("source", "editor.action.clipboardCopyAction", null);
        }
      );

      editor.addCommand(
        monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyX,
        () => {
          isInternalCopyCut.current = true;
          editor.trigger("source", "editor.action.clipboardCutAction", null);
        }
      );

      editor.onDidPaste(() => {
        if (isInternalCopyCut.current) {
          isInternalCopyCut.current = false;
        } else {
          editor.trigger("source", "undo", null);
        }
      });
    }

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      autoSaveApi();
    });

    if (monacoRef.current) {
      const monacoInstance = monacoRef.current;
      langs.map((lang: string) => {
        monacoInstance.languages.register({ id: lang });
        monacoInstance.languages.setMonarchTokensProvider(
          lang,
          TokenProvider[lang]
        );
        monacoInstance.languages.registerCompletionItemProvider(
          lang,
          CompletionItemProvider[lang]
        );
        monacoInstance.languages.setLanguageConfiguration(
          lang,
          getLanguageConfig(lang)
        );
      });
    }

    editor.updateOptions({ contextmenu: false });
  };

  const editorChange = (val: any) => {
    if (isExternalUpdateRef.current) return; // skip when we set value programmatically
    isUserTypingRef.current = false; // debounce fired, typing settled

    setIsCodeChange(true);
    const obj = {
      ...langCodeObj[questionData?.questionId as keyof typeof langCodeObj],
      [editorLanguage?.name]: val
    };
    dispatch(
      AssessmentSliceActions.mutateCandidateResponse({
        questionId: questionData.questionId,
        answerKey: [
          {
            qnVersion: questionData?.versionnumber,
            value: {
              code: encodeToBase64(val),
              language: editorLanguage?.name
            }
          }
        ]
      })
    );
    setLangCodeObj({ [questionData?.questionId]: obj });
    setEditorObj({ [questionData?.questionId]: val });
  };

  useEffect(() => {
    if (
      langCodeObj?.[questionData?.questionId] &&
      langCodeObj?.[questionData?.questionId]?.[editorLanguage?.value]
    ) {
      setEditorObj({
        [questionData?.questionId]:
          langCodeObj[questionData?.questionId][editorLanguage?.value]
      });
    } else if (savedCodeList && savedCodeList?.length > 0) {
      const temp = savedCodeList?.filter(
        (el: any) => el?.language == editorLanguage?.value
      );
      if (
        temp?.[0]?.questionid == questionData?.questionId &&
        temp?.[0]?.code
      ) {
        setEditorObj({ [questionData?.questionId]: temp?.[0]?.code });
      } else resetToStarterCode();
    } else resetToStarterCode();
  }, [editorLanguage, savedCodeList]);

  return (
    <>
      {langSelected && (
        <Editor
          height={"100%"}
          theme={editorTheme}
          // key only on questionId — no remount on theme/testcase toggle
          key={`${activeQuestionMeta?.questionId}`}
          onChange={(val: any) => {
            if (val !== undefined) {
              isUserTypingRef.current = true; // mark user is typing
              debounced(val);
            }
          }}
          // No value prop — uncontrolled, synced imperatively
          defaultValue={editorObj?.[questionData?.questionId] ?? ""}
          language={langSelected}
          defaultLanguage={langSelected}
          onMount={handleEditorDidMount}
        />
      )}
    </>
  );
};

export default EditorComponent;
